import { Box, Divider, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import LocalShippingIcon from '@material-ui/icons/LocalShipping'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import { capitalize, get } from 'lodash'
import { useEffect, useRef } from 'react'
import {
  ArrayField,
  ChipField,
  Labeled,
  NumberField,
  SingleFieldList,
  TextField,
  useRedirect,
} from 'react-admin'
import { Button } from 'shared/components/Button/Button'
import { EquipmentColorBox } from 'shared/components/Equipment/EquipmentColorBox'
import { ExternalLink } from 'shared/components/ExternalLink/ExternalLink'
import { formatReadableDate } from 'shared/helpers/dateHelpers'
import { OdometerReadingText } from 'shared/pages/TeamPage/styles'
import { bgGray, errorD1, lightGray1, midGray, successD1 } from 'shared/theme/colors'

import DocumentList from 'components/Documents/DocumentList'
import Trailer from '../../assets/images/Trailer.svg'
import { EquipmentStatus, EquipmentType } from '../../helpers/constants'
import { TRAILER, TRUCK } from '../../helpers/documents'
import { hasPermission, Permissions } from '../../Permissions'
import { CurrencyField, CustomBooleanField, CustomDateField, LabeledTypographyField } from '../Fields'
import { EquipmentQuickCreateUpdateButton } from './EquipmentQuickCreateUpdateButton'
import { InsurancePeriodSection } from './InsurancePeriodSection'
import { RemoveEquipmentButton } from './RemoveEquipmentButton'
import { UnRemoveEquipmentButton } from './UnRemoveEquipmentButton'

const useStyles = makeStyles(() => ({
  gridWrap: {
    display: 'grid',
    gridTemplateColumns: '150px 1fr',
    gap: '20px',
  },
  rightGridWrap: {
    display: 'grid',
    gridTemplateColumns: '1fr 370px',
    gap: '20px',
  },
  truckIcon: {
    width: '46px',
    height: '40px',
    color: midGray,
    marginLeft: '5px',
  },
  trailerIcon: {
    marginLeft: '5px',
  },
  flexBox: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  green: {
    color: successD1,
  },
  lightGray: {
    color: lightGray1,
  },
  topInfoGrid: {
    display: 'grid',
    gridTemplateColumns: '140px 140px 140px 140px 200px 140px 140px',
    gap: '10px',
    '& div': {
      display: 'flex',
      justifyContent: 'space-between',
    },
  },
  bottomInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 140px)',
    gap: '10px',
    '& div': {
      display: 'flex',
      justifyContent: 'space-between',
    },
  },
  filesBox: {
    padding: '15px',
    background: bgGray,
    display: 'grid',
    gap: '10px',
    gridTemplateColumns: '1fr 1fr 1fr 1fr 85px',
    alignItems: 'center',
    maxWidth: '800px',
    borderRadius: '6px',
  },
  filenameWrap: {
    maxWidth: '250px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  expirationInput: {
    margin: '0 0 20px',

    '& ::-webkit-calendar-picker-indicator': {
      filter: 'invert(70%)',
    },
  },
}))

const EquipmentSection = ({ record, permissions, equipmentType }: any) => {
  const isTruck = equipmentType === EquipmentType.Truck
  const resourceName = isTruck ? 'trucks' : 'trailers'

  if (!record || !get(record, resourceName) || !permissions) {
    return null
  }

  const canChangeEquipment = hasPermission(permissions, Permissions.ChangeEquipment)
  const equipmentList = get(record, resourceName)

  const filterAndMapEquipment = (list: any, condition: any) => {
    return list
      .filter(condition)
      .map((equipment: any) => (
        <Equipment
          key={equipment.id}
          teamId={record.id}
          equipment={equipment}
          canChangeEquipment={canChangeEquipment}
          isTruck={isTruck}
          equipmentType={equipmentType}
          label={resourceName}
          permissions={permissions}
        />
      ))
  }

  const nonRemovedEquipment = filterAndMapEquipment(
    equipmentList,
    (equipment: any) => equipment.status !== EquipmentStatus.Removed
  )

  const removedEquipment = filterAndMapEquipment(
    equipmentList,
    (equipment: any) => equipment.status === EquipmentStatus.Removed
  )

  return (
    <div style={{ paddingTop: '20px' }}>
      <style>{`.RaButton-label-7 { padding-left: 0 }`}</style>

      {nonRemovedEquipment?.length > 0 && <Box>{nonRemovedEquipment}</Box>}
      {removedEquipment?.length > 0 && (
        <Box my={2}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Removed {capitalize(resourceName)}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>{removedEquipment}</Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </div>
  )
}

const Equipment = ({ equipment, teamId, canChangeEquipment, equipmentType, isTruck, permissions }: any) => {
  const classes = useStyles()
  const wrap: any = useRef(null)

  useEffect(() => {
    const idPartOfUrl = window.location.hash.split('?')[1]
    if (idPartOfUrl) {
      const id = idPartOfUrl.replace('id=', '')
      if (id === equipment.id) {
        wrap.current?.scrollIntoView()
      }
    }
  }, [equipment.id])

  return (
    <div ref={wrap}>
      <div style={{ marginTop: '30px' }} className={classes.gridWrap}>
        <div>
          {isTruck ? (
            <LocalShippingIcon className={classes.truckIcon} />
          ) : (
            <img src={Trailer} width="30" alt="" className={classes.trailerIcon} />
          )}
          <>
            <Box mt={1} />
            <EquipmentStatusRow equipment={equipment} />
          </>
        </div>
        <div>
          <div className={classes.rightGridWrap}>
            <div>
              <TopInfo equipment={equipment} teamId={teamId} isTruck={isTruck} />
              <Box mt={4} />
              <DocumentList
                record={equipment}
                folderName={isTruck ? TRUCK : TRAILER}
                permissions={permissions}
                multipleAttachments={false}
                isEquipmentPage={true}
              />
            </div>
            <div>
              {equipment.insurance_periods && isTruck && (
                <InsurancePeriodSection
                  record={equipment}
                  canChangeEquipment={canChangeEquipment}
                  equipmentType={equipmentType}
                />
              )}
            </div>
          </div>
          <Box mt={8} />
          {isTruck && <BottomInfo equipment={equipment} />}
          <Box mt={4} />

          {canChangeEquipment && (
            <EquipmentButtonsRow
              equipment={equipment}
              equipmentType={equipmentType}
              permissions={permissions}
            />
          )}
        </div>
      </div>
      <Divider style={{ marginTop: '40px' }} />
    </div>
  )
}

const EquipmentButtonsRow = ({ equipment, equipmentType, permissions }: any) => (
  <Grid item>
    <Box display="flex" flexDirection="row">
      {equipment.status === EquipmentStatus.Inactive && (
        <RemoveEquipmentButton record={equipment} permissions={permissions} equipmentType={equipmentType} />
      )}
      {equipment.status === EquipmentStatus.Removed && (
        <UnRemoveEquipmentButton record={equipment} permissions={permissions} equipmentType={equipmentType} />
      )}
      <EquipmentQuickCreateUpdateButton record={equipment} equipmentType={equipmentType} isCreate={false} />
    </Box>
  </Grid>
)

const EquipmentStatusRow = ({ equipment }: any) => {
  const classes = useStyles()

  if (equipment.status === EquipmentStatus.Active) {
    return (
      <div className={`${classes.flexBox}`}>
        <div>
          <CheckCircleOutlineIcon fontSize="small" style={{ color: '#00AA6D' }} />
        </div>
        <div>
          <Typography variant="body1" component="span" className={classes.green}>
            Active
          </Typography>
        </div>
      </div>
    )
  }

  return (
    <Typography variant="body1" component="span">
      {capitalize(equipment.status)}
    </Typography>
  )
}

function TopInfo({ equipment, teamId, isTruck }: any) {
  const classes = useStyles()
  const redirect = useRedirect()

  const equipmentInspectionReportPath = `/teams/${teamId}/show/${
    isTruck ? EquipmentType.Truck : EquipmentType.Trailer
  }/${equipment.id}/equipment-inspection-report/`

  const hasInspectionDocument = equipment?.last_dot_inspection_document?.document_attachments?.length > 0

  const hasInspectionReport = isTruck
    ? equipment?.truck_inspection_report?.date_created
    : equipment?.trailer_inspection_report?.date_created

  const isInspectionReportOutdated = isTruck
    ? equipment?.truck_inspection_report?.inspection_report_effective_date >
      equipment?.truck_inspection_report?.relative_insp_doc_effective_date
    : equipment?.trailer_inspection_report?.inspection_report_effective_date >
      equipment?.trailer_inspection_report?.relative_insp_doc_effective_date

  const inspectionDateStr = isTruck
    ? equipment?.truck_inspection_report?.relative_insp_doc_effective_date
    : equipment?.trailer_inspection_report?.relative_insp_doc_effective_date
  const inspectionDate = inspectionDateStr ? formatReadableDate(inspectionDateStr) : ''

  return (
    <div className={classes.topInfoGrid}>
      <div>
        <LabeledTypographyField variant="body1" label="Year" value={equipment.vehicle_year} />
      </div>
      <div>
        <LabeledTypographyField variant="body1" label="Make" value={equipment.vehicle_make} />
      </div>
      <div>
        <LabeledTypographyField variant="body1" label="Model" value={equipment.vehicle_model} />
      </div>
      <div>
        <LabeledTypographyField variant="body1" label="Number" value={equipment.vehicle_number} />
      </div>
      <div>
        <LabeledTypographyField variant="body1" label="VIN Number" value={equipment.vin_number} />
      </div>
      <div>
        <LabeledTypographyField variant="body1" label="License #" value={equipment.license_plate_number} />
      </div>
      <div>
        <LabeledTypographyField variant="body1" label="License State" value={equipment.license_plate_state} />
      </div>
      {isTruck && (
        <>
          <div>
            <Labeled label="CT gets plates?">
              <CustomBooleanField source="plates_program_required" record={equipment} />
            </Labeled>
          </div>
          <div>
            <Labeled label="CT manages IFTA?">
              <CustomBooleanField source="ifta_required" record={equipment} />
            </Labeled>
          </div>
          <div>
            <Labeled label="IFTA number">
              <TextField source="ifta_sticker_number" record={equipment} />
            </Labeled>
          </div>
          <div>
            <LabeledTypographyField
              variant="body1"
              label="Vehicle Gateway"
              value={equipment.eld_serial_number}
            />
          </div>
          <div>
            <Labeled label="Vehicle ID">
              {equipment.eld_vehicles && equipment.eld_vehicles.length ? (
                <ExternalLink href={equipment.eld_vehicles[0].provider_url} variant="header3">
                  {equipment.eld_vehicles[0].provider_name}
                </ExternalLink>
              ) : (
                <></>
              )}
            </Labeled>
          </div>
          <div>
            <Labeled label="Assigned Drivers">
              <ArrayField source="drivers" record={equipment}>
                <SingleFieldList linkType={false}>
                  <ChipField clickable={false} source="full_name" />
                </SingleFieldList>
              </ArrayField>
            </Labeled>
          </div>
        </>
      )}
      {!isTruck && (
        <>
          <div>
            <LabeledTypographyField variant="body1" label="Ownership Type" value={equipment.ownership_type} />
          </div>
          {equipment.weight_lb && (
            <div>
              <Labeled label="Weight lbs">
                <NumberField source="weight_lb" record={equipment} />
              </Labeled>
            </div>
          )}
          {equipment.length_ft && (
            <div>
              <Labeled label="Length ft">
                <NumberField source="length_ft" record={equipment} />
              </Labeled>
            </div>
          )}
          {equipment.source && (
            <div>
              <LabeledTypographyField variant="body1" label="Source" value={equipment.source} />
            </div>
          )}
          {equipment.internal_notes && (
            <div>
              <Labeled label="Internal notes">
                <TextField
                  record={equipment}
                  source="internal_notes"
                  aria-multiline
                  fullWidth
                  component="pre"
                />
              </Labeled>
            </div>
          )}
        </>
      )}
      {
        <div>
          <Labeled label="Tire & brake levels">
            <>
              {!hasInspectionDocument || (hasInspectionDocument && !hasInspectionReport) ? (
                <Typography variant="body1" className={classes.lightGray}>
                  -
                </Typography>
              ) : isInspectionReportOutdated ? (
                <Typography variant="inherit" className={classes.lightGray}>
                  {`${inspectionDate} (Outdated)`}
                </Typography>
              ) : (
                <Button
                  onClick={() => redirect(equipmentInspectionReportPath)}
                  variant="text"
                  type="button"
                  size="small"
                >
                  {inspectionDate}
                </Button>
              )}
              {((hasInspectionDocument && !hasInspectionReport) || isInspectionReportOutdated) && (
                <Button
                  onClick={() => redirect(equipmentInspectionReportPath)}
                  variant="primary"
                  margin="0 auto"
                  type="button"
                  size="small"
                >
                  Add new
                </Button>
              )}
            </>
          </Labeled>
        </div>
      }
      {equipment.vehicle_color && (
        <div>
          <LabeledTypographyField
            variant="body1"
            label="Vehicle color"
            value={<EquipmentColorBox color={equipment.vehicle_color} />}
          />
        </div>
      )}
    </div>
  )
}

function BottomInfo({ equipment }: any) {
  const classes = useStyles()
  return (
    <div className={classes.bottomInfoGrid}>
      <div>
        <LabeledTypographyField variant="body1" label="Ownership Type" value={equipment.ownership_type} />
      </div>
      {equipment.odometer_reading_mi && (
        <Labeled label="Odometer Reading (Miles)">
          <OdometerReadingText showErrorColor={equipment.odometer_reading_mi > 400000} errorColor={errorD1}>
            {equipment.odometer_reading_mi}
            {equipment.odometer_reading_mi_date && (
              <>
                <br />
                (Updated: {new Date(equipment.odometer_reading_mi_date).toLocaleDateString()})
              </>
            )}
          </OdometerReadingText>
        </Labeled>
      )}

      {equipment.weight_lb && (
        <div>
          <Labeled label="Weight lbs">
            <NumberField source="weight_lb" record={equipment} />
          </Labeled>
        </div>
      )}
      {equipment.plates_program_required && (
        <>
          <div>
            <Labeled label="Date purchased or leased">
              <CustomDateField source="date_acquired" record={equipment} />
            </Labeled>
          </div>
          <div>
            <Labeled label="Price purchased or leased">
              <CurrencyField source="price_dollars" record={equipment} />
            </Labeled>
          </div>
        </>
      )}
      {equipment.source && (
        <div>
          <LabeledTypographyField variant="body1" label="Source" value={equipment.source} />
        </div>
      )}
      {equipment.status_reason && (
        <div>
          <LabeledTypographyField variant="body1" label="Status reason" value={equipment.status_reason} />
        </div>
      )}
      <div>
        <Labeled label="Internal notes">
          <TextField record={equipment} source="internal_notes" aria-multiline fullWidth component="pre" />
        </Labeled>
      </div>
    </div>
  )
}

export default EquipmentSection
